import { Card, Modal, Table, Button } from 'antd';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import FullScreenSpinner from '../../Layout/FullScreenSpinner';
import { errHandler } from '../../helper/helper';
import { getDailyWorkCashTransaction, getDailyWorkNonCashTransaction, dailyWorkDetailsReport } from './GeneralService';
import { generatePDF } from '../../Utils/pdfExport';
import { generateBlob } from '../../Utils/CreateBlob';

const LEDGER_RANGES = [
  { min: 1000, max: 1999, label: "1000-1999" },
  { min: 2000, max: 2999, label: "2000-2999" },
  { min: 3000, max: 3999, label: "3000-3999" },
  { min: 4000, max: 4999, label: "4000-4999" },
  { min: 5000, max: 5999, label: "5000-5999" },
];

const getLedgerCodeNumber = (code) => {
  const num = parseInt(code, 10);
  return isNaN(num) ? null : num;
};

const getRangeLabel = (code) => {
  const num = getLedgerCodeNumber(code);
  if (num === null) return null;
  for (const range of LEDGER_RANGES) {
    if (num >= range.min && num <= range.max) return range.label;
  }
  return null;
};

const createData = (data) => {
  let convertedData = (data.map((post) => {
    return {
      number: post.number,
      details: post.details,
      amount: post.amount
    }
  }))
  return convertedData;
}

const DailyWorkTable = () => {
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [selectedCell, setSelectedCell] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [date, setDate] = useState(null);
  const [subData, setSubData] = useState([]);
  const [columnIndex, setColumnIndex] = useState(null);
  const [subHeader, setSubHeader] = useState(null);
  const [spinning, setSpinning] = useState(false);

  const branchId = localStorage.getItem("branchId") ? localStorage.getItem("branchId") : '';
  const branchName = localStorage.getItem("branch") ? localStorage.getItem("branch") : '';

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      render: (_, record, index) => record.ledgerCode && record.ledgerCode.startsWith('Subtotal') ? '' : index + 1,
    },
    {
      title: 'LedgerCode',
      dataIndex: 'ledgerCode',
      key: 'ledgerCode',
    },
    {
      title: 'Cash Transaction',
      children: [
        {
          title: 'Debit No',
          dataIndex: 'cashDebitCount',
          key: 'cashDebitCount',
          align: 'right',
        },
        {
          title: 'Debit Amount',
          dataIndex: 'cashDebit',
          key: 'cashDebit',
          align: 'right',
          render: (val) => (val != null ? Number(val).toFixed(2) : ""),
          onCell: (record) => ({
            onClick: () => handleCellClick(record.ledgerCode, 'cashDebit'),
          }),
        },
        {
          title: 'Credit No',
          dataIndex: 'cashCreditCount',
          key: 'cashCreditCount',
          align: 'right',
        },
        {
          title: 'Credit Amount',
          dataIndex: 'cashCredit',
          key: 'cashCredit',
          align: 'right',
          render: (val) => (val != null ? Number(val).toFixed(2) : ""),
          onCell: (record) => ({
            onClick: () => handleCellClick(record.ledgerCode, 'cashCredit'),
          }),
        },
      ],
    },
    {
      title: 'Non Cash Transaction',
      children: [
        {
          title: 'Debit No',
          dataIndex: 'nonCashDebitCount',
          key: 'nonCashDebitCount',
          align: 'right',
        },
        {
          title: 'Debit Amount',
          dataIndex: 'nonCashDebit',
          key: 'nonCashDebit',
          align: 'right',
          render: (val) => (val != null ? Number(val).toFixed(2) : ""),
          onCell: (record) => ({
            onClick: () => handleCellClick(record.ledgerCode, 'nonCashDebit'),
          }),
        },
        {
          title: 'Credit No',
          dataIndex: 'nonCashCreditCount',
          key: 'nonCashCreditCount',
          align: 'right',
        },
        {
          title: 'Credit Amount',
          dataIndex: 'nonCashCredit',
          key: 'nonCashCredit',
          align: 'right',
          render: (val) => (val != null ? Number(val).toFixed(2) : ""),
          onCell: (record) => ({
            onClick: () => handleCellClick(record.ledgerCode, 'nonCashCredit'),
          }),
        },
      ],
    },
  ];

  const subColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      render: (_, record, index) => index + 1,
    },
    {
      title: 'Number',
      dataIndex: 'number',
      key: 'number',
    },
    {
      title: 'Details',
      dataIndex: 'details',
      key: 'details',
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
    },
  ];

  // const handleCellClick = (cellValue, columnIndex) => {
  //   setSelectedCell(cellValue);
  //   setColumnIndex(columnIndex)
  //   if (columnIndex.substring(0, 3) === "non") {
  //     const clarify = (columnIndex.substring(3) === "CashCredit") ? "C" : "D";
  //     let data={
  //       branchId:branchId,
  //       selectedDate:date,
  //       ledgerCode:selectedCell,
  //       cdType:clarify
  //     };
  //     getAllNonCashTransaction(data);
  //   } else {
  //     const clarify = (columnIndex === "cashCredit") ? "C" : "D";
  //     let data={
  //       branchId:branchId,
  //       selectedDate:date,
  //       ledgerCode:selectedCell,
  //       cdType:clarify
  //     }
  //     getAllCashTransaction(data);
  //   }
  //   setModalIsOpen(true);
  // };

  const handleCellClick = (cellValue, columnIndex) => {
    setSelectedCell(cellValue);
    setColumnIndex(columnIndex);

    let data = {
      branchId: branchId,
      selectedDate: date,
      ledgerCode: cellValue,
      cdType: determineCdType(columnIndex)
    };

    if (isNonCashColumn(columnIndex)) {
      getAllNonCashTransaction(data);
    } else {
      getAllCashTransaction(data);
    }
    // setSelectedCell(null);
  };

  const determineCdType = (columnIndex) => {
    if (isNonCashColumn(columnIndex)) {
      return columnIndex.substring(3) === "CashCredit" ? "C" : "D";
    } else {
      return columnIndex === "cashCredit" ? "C" : "D";
    }
  };

  const isNonCashColumn = (columnIndex) => columnIndex.substring(0, 3) === "non";

  const handleDailyWorkPdfDownload = () => {
    const data = { branchId, selectedDate: date };
    dailyWorkDetailsReport(data).then((res) => {
      generateBlob(res.data);
    }).catch((err) => {
      errHandler(err);
    });
  };

  const handleCloseModal = () => {
    setModalIsOpen(false);
  };
  const location = useLocation();
  const navigate = useNavigate();



  const totalAmount = subData.reduce((acc, record) => acc + record.amount, 0).toFixed(2);

  const summarySubRow = {
    id: " ",
    number: " ",
    details: 'Total',
    amount: totalAmount,
    isSummaryRow: true
  }

  const rowClassName = (record) => {
    if (record.ledgerCode && record.ledgerCode.startsWith('Subtotal')) return 'subtotal-row';
    if (record.ledgerCode === 'Grand Total') return 'summary-row';
    return '';
  };

  const dataSourceWithSummary = tableData;
  const dataSourceWithSubSummary = [...subData, summarySubRow];

  const reportDate = location.state.selectedDate.$d
  const formattedDate = moment(reportDate).format("yyyy-MM-DD");

  const getDataFromLocation = () => {
    if (location.state) {
      setDate(formattedDate)
      setTableData(location.state.tableData)
    } else {
      console.log('No data in location.state');
    }
  };
  const getAllCashTransaction = (data) => {
    setSpinning(true);
    getDailyWorkCashTransaction(data).then((res => {
      setSubHeader(res.data.ledgerName);
      let data = createData(res.data.data);
      setSubData(data);
      setModalIsOpen(true);
    })).catch((err) => {
      errHandler(err);
    }).finally(() => {
      setSpinning(false);
    })
  };

  const getAllNonCashTransaction = (data) => {
    setSpinning(true);
    getDailyWorkNonCashTransaction(data).then((res => {
      setSubHeader(res.data.ledgerName);
      let data = createData(res.data.data);
      setSubData(data);
      setModalIsOpen(true);
    })).catch((err) => {
      errHandler(err);
    }).finally(() => {
      setSpinning(false);
    })
  };

  useEffect(() => {
    getDataFromLocation();
  }, [location]);

  return (
    <div style={{ padding: 10 }}>
      <Card style={{ border: '1px solid #f0f0f0', borderRadius: '5px', boxShadow: '0 2px 8px rgba(51, 39, 39, 0.1)' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
          <Button type="default" onClick={() => navigate(-1)} style={{ marginRight: 10 }}>
            Back
          </Button>
          <h2 style={{ margin: 0, flex: 1, textAlign: 'center' }}>{branchName} - Samurdhi bank Daily Report - {date}</h2>
          <Button type="primary" onClick={handleDailyWorkPdfDownload}>
            Export PDF
          </Button>
        </div>
        <div style={{ position: 'relative' }}>
          <FullScreenSpinner spinning={spinning} />
          <Table dataSource={dataSourceWithSummary}
            columns={columns}
            bordered pagination={false}
            rowClassName={rowClassName}
            className='daily-work-table'
          />
        </div>
      </Card>

      {modalIsOpen && (
        <Modal
          open={modalIsOpen}
          title={""}
          closable={true}
          footer={null}
          onCancel={handleCloseModal}
          width={1050}
        >
           <div align="right" style={{ marginRight: 50 }}>
            <Button
              onClick={() =>
                generatePDF(`${branchName} - ${selectedCell}-${subHeader} - ${date}`, subColumns, dataSourceWithSubSummary)
              }
              type="primary"
              style={{ marginBottom: 10 }}
            >
              Export PDF
            </Button>
          </div>
          <div>
            <div align="center" >
              <h4>{`${branchName} - ${selectedCell}-${subHeader}-${date}`}</h4>
            </div>

            <Table dataSource={dataSourceWithSubSummary} columns={subColumns} bordered pagination={false}
              rowClassName={(record) => record.isSummaryRow ? 'summary-row' : ''} className='daily-work-table'
            />
          </div>
        </Modal>
      )}
    </div>
  );
};

export default DailyWorkTable;
